(function () {
  const MV_DEFINE_TIMEOUT_MS = 12000;

  function loadModelViewerOnce() {
    if (customElements.get('model-viewer')) {
      window.__youpModelViewerPromise = Promise.resolve();
      return window.__youpModelViewerPromise;
    }
    const pending = window.__youpModelViewerPromise;
    if (pending) return pending;

    const p = Promise.race([
      customElements.whenDefined('model-viewer'),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('model-viewer define timeout')), MV_DEFINE_TIMEOUT_MS);
      }),
    ]);
    p.catch(() => {
      window.__youpModelViewerPromise = null;
    });
    window.__youpModelViewerPromise = p;
    return p;
  }

  const CONNECTOR_PAD = 10;

  function panelEdgeTowardHotspot(hx, hy, rel) {
    const midX = (rel.left + rel.right) / 2;
    const midY = (rel.top + rel.bottom) / 2;
    const vx = hx - midX;
    const vy = hy - midY;
    if (Math.abs(vx) >= Math.abs(vy)) {
      if (vx <= 0) {
        return {
          x: rel.left,
          y: Math.min(Math.max(hy, rel.top + CONNECTOR_PAD), rel.bottom - CONNECTOR_PAD),
        };
      }
      return {
        x: rel.right,
        y: Math.min(Math.max(hy, rel.top + CONNECTOR_PAD), rel.bottom - CONNECTOR_PAD),
      };
    }
    if (vy <= 0) {
      return {
        x: Math.min(Math.max(hx, rel.left + CONNECTOR_PAD), rel.right - CONNECTOR_PAD),
        y: rel.top,
      };
    }
    return {
      x: Math.min(Math.max(hx, rel.left + CONNECTOR_PAD), rel.right - CONNECTOR_PAD),
      y: rel.bottom,
    };
  }

  function parseOrbitRadius(orbitStr) {
    if (!orbitStr) return 'auto';
    const parts = orbitStr.trim().split(/\s+/);
    return parts[2] || 'auto';
  }

  function scaleOrbitRadius(radius, scalePercent) {
    const scale = (Number(scalePercent) || 100) / 100;
    if (!radius || radius === 'auto' || scale >= 0.999) return radius;
    const match = String(radius)
      .trim()
      .match(/^([\d.]+)\s*(%|m)$/i);
    if (!match) return radius;
    const val = Math.round(parseFloat(match[1]) * scale * 1000) / 1000;
    return `${val}${match[2]}`;
  }

  function applyOrbitRadius(mv, orbitStr, radius) {
    const parts = orbitStr.trim().split(/\s+/);
    const next = `${parts[0] || 'auto'} ${parts[1] || 'auto'} ${radius}`;
    mv.cameraOrbit = next;
    mv.setAttribute('camera-orbit', next);
    return next;
  }

  function frameModelViewer(mv) {
    if (!mv || typeof mv.getBoundingBoxCenter !== 'function') return;

    const root = mv.closest('[data-youp-3d-section]');
    const orbit = root?.getAttribute('data-youp-3d-camera-orbit')?.trim();

    try {
      const center = mv.getBoundingBoxCenter();
      const tx = center.x.toFixed(4);
      const ty = center.y.toFixed(4);
      const tz = center.z.toFixed(4);

      mv.cameraTarget = `${tx}m ${ty}m ${tz}m`;

      let orbitLock = root?.getAttribute('data-youp-3d-camera-orbit-lock')?.trim();

      const zoomScale = root?.getAttribute('data-youp-3d-camera-zoom-scale');
      let framedOrbit = orbit || '';

      if (orbit) {
        const parts = orbit.trim().split(/\s+/);
        let radius = parts[2] || 'auto';
        framedOrbit = `${parts[0]} ${parts[1]} ${radius}`;
        mv.cameraOrbit = framedOrbit;
        mv.setAttribute('camera-orbit', framedOrbit);
      }

      mv.jumpCameraToGoal?.();

      let lockRadius = parseOrbitRadius(orbitLock || framedOrbit);
      if (lockRadius === 'auto') {
        lockRadius = parseOrbitRadius(mv.cameraOrbit || mv.getAttribute('camera-orbit') || '');
      }
      lockRadius = scaleOrbitRadius(lockRadius, zoomScale);
      if (lockRadius !== 'auto' && framedOrbit) {
        framedOrbit = applyOrbitRadius(mv, framedOrbit, lockRadius);
        mv.jumpCameraToGoal?.();
      }

      if (orbitLock) {
        const lockParts = orbitLock.trim().split(/\s+/);
        orbitLock = `${lockParts[0] || 'auto'} ${lockParts[1] || 'auto'} ${lockRadius}`;
        root?.setAttribute('data-youp-3d-camera-orbit-lock', orbitLock);
        mv.setAttribute('min-camera-orbit', orbitLock);
        mv.setAttribute('max-camera-orbit', orbitLock);
      }

      const fovRaw = root?.getAttribute('data-youp-3d-field-of-view');
      const fovDeg = Math.min(40, Math.max(18, Number.parseInt(fovRaw, 10) || 24));
      const fov = `${fovDeg}deg`;
      mv.fieldOfView = fov;
      mv.setAttribute('min-field-of-view', fov);
      mv.setAttribute('max-field-of-view', fov);

      mv.jumpCameraToGoal?.();
    } catch {
      /* model-viewer may not be ready */
    }
  }

  function bindYoup3dConnector(root, signal, mv) {
    const stage = root.querySelector('[data-youp-3d-stage]');
    const svg = root.querySelector('[data-youp-3d-connector]');
    const line = root.querySelector('[data-youp-3d-connector-line]');
    if (!stage || !svg || !line) {
      root._youp3dScheduleConnector = () => {};
      return;
    }

    let raf = null;

    const updateConnector = () => {
      if (!root.classList.contains('youp-3d--has-open-hotspot')) {
        svg.classList.remove('is-visible');
        return;
      }
      if (window.matchMedia('(max-width: 999px)').matches) {
        svg.classList.remove('is-visible');
        return;
      }

      const activeBtn = root.querySelector('[data-youp-3d-hotspot].is-active');
      const panel = root.querySelector('.youp-3d__detail-card.is-active');
      if (!activeBtn || !panel) {
        svg.classList.remove('is-visible');
        return;
      }

      const w = Math.round(stage.clientWidth);
      const h = Math.round(stage.clientHeight);
      if (w < 8 || h < 8) {
        svg.classList.remove('is-visible');
        return;
      }

      svg.setAttribute('width', String(w));
      svg.setAttribute('height', String(h));
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);

      const sr = stage.getBoundingClientRect();
      const ar = activeBtn.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();

      const hx = ar.left - sr.left + ar.width / 2;
      const hy = ar.top - sr.top + ar.height / 2;
      const rel = {
        left: pr.left - sr.left,
        right: pr.right - sr.left,
        top: pr.top - sr.top,
        bottom: pr.bottom - sr.top,
      };
      const end = panelEdgeTowardHotspot(hx, hy, rel);

      line.setAttribute('x1', hx.toFixed(1));
      line.setAttribute('y1', hy.toFixed(1));
      line.setAttribute('x2', end.x.toFixed(1));
      line.setAttribute('y2', end.y.toFixed(1));
      svg.classList.add('is-visible');
    };

    const scheduleConnector = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        updateConnector();
      });
    };

    const ro = new ResizeObserver(scheduleConnector);
    ro.observe(stage);
    const aside = root.querySelector('[data-youp-3d-detail]');
    if (aside) ro.observe(aside);

    const onScrollResize = () => scheduleConnector();
    window.addEventListener('resize', onScrollResize, { signal });
    window.addEventListener('scroll', onScrollResize, { capture: true, passive: true, signal });
    const enforceOrbitLock = () => {
      const orbitLock = root.getAttribute('data-youp-3d-camera-orbit-lock')?.trim();
      if (!orbitLock) return;
      if (mv.getAttribute('max-camera-orbit') !== orbitLock) {
        mv.setAttribute('min-camera-orbit', orbitLock);
        mv.setAttribute('max-camera-orbit', orbitLock);
      }
    };
    mv.addEventListener('camera-change', enforceOrbitLock, { signal });

    mv.addEventListener('camera-change', scheduleConnector, { signal });

    const onAbort = () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      svg.classList.remove('is-visible');
      root._youp3dScheduleConnector = null;
    };
    signal.addEventListener('abort', onAbort, { once: true });

    root._youp3dScheduleConnector = scheduleConnector;
    scheduleConnector();
  }

  function scrollMobileCardIntoViewIfNeeded(root, idStr) {
    if (!idStr || !window.matchMedia('(max-width: 999px)').matches) return;

    const activeCard = root.querySelector(`[data-youp-3d-detail-card="${idStr}"].is-active`);
    if (!activeCard) return;

    const rect = activeCard.getBoundingClientRect();
    const margin = 16;
    const outOfView =
      rect.top < margin || rect.bottom > window.innerHeight - margin || rect.height < 8;
    if (!outOfView) return;

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function setActive(root, rawId, options = {}) {
    const idStr = rawId == null || rawId === '' ? '' : String(rawId);
    const detail = root.querySelector('[data-youp-3d-detail]');
    const hotspotButtons = root.querySelectorAll('[data-youp-3d-hotspot]');
    const cards = root.querySelectorAll('[data-youp-3d-detail-card]');

    hotspotButtons.forEach((btn) => {
      const bid = btn.getAttribute('data-youp-3d-hotspot');
      const isOn = idStr !== '' && bid === idStr;
      btn.setAttribute('aria-expanded', isOn ? 'true' : 'false');
      btn.classList.toggle('is-active', isOn);
    });
    cards.forEach((card) => {
      const cid = card.getAttribute('data-youp-3d-detail-card');
      const match = idStr !== '' && cid === idStr;
      card.classList.toggle('is-active', match);
      card.setAttribute('aria-hidden', match ? 'false' : 'true');
    });
    root.classList.toggle('youp-3d--has-open-hotspot', idStr !== '');

    if (idStr && detail) {
      detail.scrollTop = 0;
    }

    if (options.scrollToCard) {
      scrollMobileCardIntoViewIfNeeded(root, idStr);
    }

    root._youp3dScheduleConnector?.();
  }

  function bindHotspotAutoplay(root, signal) {
    if (root.getAttribute('data-youp-3d-hotspot-autoplay') === 'false') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    /* Autoplay + scroll-into-view hijacks mobile reading; desktop hover handles discovery */
    if (window.matchMedia('(max-width: 999px)').matches) return;

    const buttons = [...root.querySelectorAll('[data-youp-3d-hotspot]')];
    if (buttons.length === 0) return;

    const intervalMs = Math.max(
      2000,
      Number.parseInt(root.getAttribute('data-youp-3d-hotspot-autoplay-ms'), 10) || 2500
    );
    let index = 0;
    let timer = null;
    let paused = false;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const scheduleNext = () => {
      clearTimer();
      if (paused || signal.aborted) return;
      timer = setTimeout(() => {
        const id = buttons[index].getAttribute('data-youp-3d-hotspot');
        if (id) setActive(root, id);
        index = (index + 1) % buttons.length;
        scheduleNext();
      }, intervalMs);
    };

    const pause = () => {
      paused = true;
      clearTimer();
    };

    const resume = () => {
      if (!paused || signal.aborted) return;
      paused = false;
      scheduleNext();
    };

    const firstId = buttons[0].getAttribute('data-youp-3d-hotspot');
    if (firstId) setActive(root, firstId);
    index = 1;
    scheduleNext();

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      root.addEventListener('pointerenter', pause, { signal, capture: true });
      root.addEventListener('pointerleave', resume, { signal });
    }
    root.addEventListener(
      'focusin',
      (e) => {
        if (e.target.closest('[data-youp-3d-hotspot], [data-youp-3d-detail], [data-youp-3d-close]')) pause();
      },
      { signal, capture: true }
    );
    root.addEventListener(
      'focusout',
      (e) => {
        if (!root.contains(e.relatedTarget)) resume();
      },
      { signal }
    );

    signal.addEventListener('abort', clearTimer, { once: true });
  }

  function initSection(root) {
    const ac = new AbortController();
    if (root._youp3dAbo) {
      root._youp3dAbo.abort();
    }
    root._youp3dAbo = ac;
    const { signal } = ac;

    const canHoverHotspot = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const activateHotspot = (btn) => {
      const hid = btn.getAttribute('data-youp-3d-hotspot');
      if (hid == null || hid === '') return;
      setActive(root, hid);
    };

    const deactivateUnlessMovingToHotspotUi = (e) => {
      const related = e.relatedTarget;
      if (related?.closest?.('[data-youp-3d-hotspot], [data-youp-3d-detail]')) return;
      setActive(root, '');
    };

    root.querySelectorAll('[data-youp-3d-hotspot]').forEach((btn) => {
      if (canHoverHotspot) {
        btn.addEventListener(
          'pointerenter',
          () => {
            activateHotspot(btn);
          },
          { signal }
        );
        btn.addEventListener('pointerleave', deactivateUnlessMovingToHotspotUi, { signal });
      }
    });

    root.addEventListener(
      'click',
      (e) => {
        const fsBtn = e.target.closest('[data-youp-3d-fullscreen]');
        if (fsBtn) {
          const fsTarget = root.querySelector('[data-youp-3d-fullscreen-target]');
          if (!fsTarget) return;
          e.preventDefault();
          if (document.fullscreenElement) {
            document.exitFullscreen?.();
          } else {
            fsTarget.requestFullscreen?.().catch(() => {});
          }
          return;
        }

        const closeBtn = e.target.closest('[data-youp-3d-close]');
        if (closeBtn) {
          e.preventDefault();
          setActive(root, '');
          return;
        }

        const btn = e.target.closest('[data-youp-3d-hotspot]');
        if (btn && root.contains(btn)) {
          e.preventDefault();
          const hid = btn.getAttribute('data-youp-3d-hotspot');
          if (hid == null || hid === '') return;
          if (canHoverHotspot) {
            activateHotspot(btn);
            return;
          }
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          setActive(root, isOpen ? '' : hid, { scrollToCard: !isOpen });
        }
      },
      { signal }
    );

    root.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') setActive(root, '');
      },
      { signal }
    );

    const mv = root.querySelector('model-viewer');
    if (!mv) return;

    const errEl = root.querySelector('[data-youp-3d-load-error]');
    const modelErrText = errEl ? errEl.textContent : '';

    const onLoad = () => {
      frameModelViewer(mv);
      root.classList.add('youp-3d--model-loaded');
      if (errEl) errEl.hidden = true;
      root._youp3dScheduleConnector?.();
      bindHotspotAutoplay(root, signal);
    };

    const onError = () => {
      root.classList.add('youp-3d--mv-error');
      root.classList.remove('youp-3d--model-loaded');
      if (errEl) {
        if (modelErrText) errEl.textContent = modelErrText;
        errEl.hidden = false;
      }
    };

    mv.addEventListener('load', onLoad, { signal });
    mv.addEventListener('error', onError, { signal });

    bindYoup3dConnector(root, signal, mv);

    loadModelViewerOnce()
      .then(() => {
        requestAnimationFrame(() => {
          if (signal.aborted) return;
          if (typeof mv.loaded === 'boolean' && mv.loaded) {
            onLoad();
          }
        });
      })
      .catch(() => {
        if (!signal.aborted) {
          root.classList.add('youp-3d--mv-error');
          if (errEl) {
            const scriptMsg = root.getAttribute('data-youp-3d-script-error-text');
            if (scriptMsg) errEl.textContent = scriptMsg;
            errEl.hidden = false;
          }
        }
      });
  }

  function resetSection(root) {
    if (root._youp3dAbo) {
      root._youp3dAbo.abort();
      root._youp3dAbo = null;
    }
    root.classList.remove('youp-3d--model-loaded', 'youp-3d--mv-error', 'youp-3d--has-open-hotspot');
    setActive(root, '');
  }

  function initAll() {
    document.querySelectorAll('[data-youp-3d-section]').forEach((root) => {
      resetSection(root);
      initSection(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root =
      e.target?.querySelector?.('[data-youp-3d-section]') ||
      (e.target?.matches?.('[data-youp-3d-section]') ? e.target : null);
    if (!root) return;
    resetSection(root);
    initSection(root);
  });
})();
